import { ProductType } from '@/enums';
import { DateRange, dateRange } from '@/pages/authenPages/Dashboard/utils';
import { DashboardOperationsParams } from '@/services/amili-v3-services/reports.service';
import { CustomResponse } from '@/services/api-services/type';
import { DefaultEntity } from '@/types';
import { DEFAULT_PAGE_SIZE, PAGE_DEFAULT } from '@amili-v3/amili-v3-design';
import { InfiniteData } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { DefaultParams } from './queryKeys';

type ReturnPageParamsType<K extends DefaultParams> = K &
  Required<Pick<K, 'page' | 'take'>>;

export const passPageParams = <P extends DefaultParams>(
  params: P,
): ReturnPageParamsType<P> => ({
  ...params,
  page: Number(params?.page) || PAGE_DEFAULT,
  take: Number(params?.take) || DEFAULT_PAGE_SIZE,
});

export const formatInfiniteQueryData = <E extends DefaultEntity>(
  data: InfiniteData<CustomResponse<E[]>, unknown> | undefined,
) => {
  return data ? data.pages.flatMap(page => page.data) : [];
};

export function buildApiParams(
  channelId: string,
  range: DateRange,
  customDates: [string, string],
  sequencingType?: string,
  productName?: string,
  productType?: ProductType,
): DashboardOperationsParams {
  // Use UTC to avoid local-timezone offset shifting calendar boundaries.
  const now = dayjs.utc();

  const base = {
    channelId,
    sequencingType: sequencingType || undefined,
    productName: productName || undefined,
    productType: productType || undefined,
  };

  switch (range) {
    case dateRange.pastWeek:
      return {
        ...base,
        startDate: now.startOf('isoWeek').toISOString(),
        endDate: now.endOf('day').toISOString(),
        separate: 'day',
        comparison: 'week',
      };
    case dateRange.pastMonth:
      return {
        ...base,
        startDate: now.startOf('month').toISOString(),
        endDate: now.endOf('day').toISOString(),
        separate: 'day',
        comparison: 'month',
      };
    case dateRange.pastYear:
      return {
        ...base,
        startDate: now.startOf('year').toISOString(),
        endDate: now.endOf('day').toISOString(),
        separate: 'month',
        comparison: 'year',
      };
    case dateRange.customRange: {
      const start = dayjs(customDates[0]);
      const end = dayjs(customDates[1]);
      const diffDays = end.diff(start, 'day');
      const separate =
        diffDays > 365 ? 'year' : diffDays > 60 ? 'month' : 'day';
      return {
        ...base,
        startDate: start.startOf('day').toISOString(),
        endDate: end.endOf('day').toISOString(),
        separate,
        comparison: 'none',
      };
    }
    default:
      return {
        ...base,
        startDate: now.startOf('month').toISOString(),
        endDate: now.endOf('day').toISOString(),
        separate: 'day',
        comparison: 'month',
      };
  }
}
